import React from 'react';
import {
  Box, Skeleton, Table, TableHead, TableRow, TableCell, TableBody, Paper, useTheme,
} from '@mui/material';
import Panel from '../components/common/Panel';

/**
 * TableSkeleton — Ladezustand einer TABELLENFLAECHE.
 *
 * Der Ersatz fuer den zentrierten Kreisel an Stellen, an denen die ganze
 * Flaeche wartet und geladen eine Tabelle steht (`anti-patterns.md` §11:
 * „Skelett statt Spinner" beim Seitenaufbau). Die Form folgt der geladenen
 * Tabelle: derselbe Rahmen, dieselbe `<Table>`, dieselbe Spaltenzahl — sonst
 * springt die Seite, wenn die Daten eintreffen
 * (`tests/design/skelett-anatomie.spec.ts`).
 *
 * 🔴 Der RAHMEN ist der des Aufrufers, nicht eine zweite Bauart — derselbe
 * Schalter wie in `KpiCardsSkeleton`: `framed` (Vorgabe) zeichnet die
 * Hausflaeche `Panel` (`ops/DepHealth.jsx:636`, `rma/RMAReports.jsx:545`),
 * `paper` die `Paper variant="outlined"`-Flaeche der geladenen Tabelle
 * (`ops/PoAnomalies.jsx:298`, `borderRadius: 2` = 12 px). Ein nachgebauter
 * Rahmen wiche beim Eintreffen der Daten ab — Schatten und Radius —, und genau
 * den Sprung soll das Geruest verhindern.
 *
 * 🔴 Drei Rahmenformen, weil es drei geladene Flaechen gibt. `paper="elevation"`
 * ist die dritte: das SCHLICHTE MUI-`Paper` der geladenen Tabelle, die in einem
 * `TableContainer component={Paper}` steht (`pages/taxonomy/RelationsManagerTab.jsx:855`,
 * `ListView`) — MUI-Vorgabe `elevation={1}` (`shadows[1]`) und Theme-Radius,
 * also weder der `Panel`-Rahmen (Radius 18, Panel-Schatten, 1-px-Rahmen) noch
 * die `outlined`-Form (kein Schatten, 1-px-Rahmen, Radius 12). Der Radius wird
 * hier NICHT gesetzt: MUI nimmt fuer ein `Paper` ohne `sx` genau
 * `theme.shape.borderRadius` (6 px, `theme.js:323`) — also den Wert, den auch
 * die geladene Flaeche traegt. `overflowX: 'auto'` spiegelt den
 * `TableContainer`, der bei schmalen Breiten scrollt statt abzuschneiden.
 *
 * 🔴 KEIN erfundener Text. Die Kopfzeile traegt Balken, keine Etiketten: welche
 * Spalten geladen erscheinen, ist eine Aussage ueber Daten, die noch nicht da
 * sind (dieselbe Begruendung wie `KennzahlZelle` in `CompetitorDetailSkeleton`).
 * Wo die echten Etiketten statisch feststehen, zeichnet der Aufrufer sie selbst.
 *
 * 🔴 Jede Kopfzelle traegt `data-skelett-spalte`. Das ist der stabile Zaehlhaken
 * der Form-Probe (`tests/design/skelett-anatomie.spec.ts`, Faelle `S.1`): sie
 * liest die Spaltenzahl des Geruests und vergleicht sie mit der geladenen
 * Flaeche. Ohne einen eigenen Haken faellt die Zaehlung unter „Form stimmt" und
 * „beliebiger Balkenstapel" gleich aus (Regel 17 §3b).
 *
 * `role="status"` + `aria-label` + `aria-busy` melden den Ladezustand
 * Hilfsmitteln an, wie es die uebrigen Gerueste unter `Skeletons/` tun.
 *
 * Werte aus `theme/tokens` — Rule 22 laesst neue Literale nicht zu.
 *
 * @props
 *   columns   — Spaltenzahl der geladenen Tabelle (Vorgabe 5)
 *   rows      — sichtbare Platzhalterzeilen (Vorgabe 5, kein Versprechen)
 *   framed    — `true` (Vorgabe) setzt den `Panel`-Rahmen wie die geladene
 *               Flaeche; `false` zeichnet nur die Tabelle (fuer Aufrufer, deren
 *               geladene Tabelle selbst in einem `TableViewport` steht)
 *   paper     — `true`/`'outlined'` zeichnet `Paper variant="outlined"` statt
 *               `Panel` (fuer Aufrufer, deren geladene Tabelle so gerahmt ist);
 *               `'elevation'` zeichnet das schlichte MUI-`Paper` eines
 *               `TableContainer component={Paper}`; schlaegt `framed`
 *   ariaLabel — zugaenglicher Name des Ladezustands (Vorgabe „Loading table")
 */
export default function TableSkeleton({
  columns = 5,
  rows = 5,
  framed = true,
  paper = false,
  ariaLabel = 'Loading table',
}) {
  const theme = useTheme();
  const tabelle = (
    <Table size="small">
      <TableHead>
        <TableRow>
          {[...Array(columns)].map((_, c) => (
            <TableCell key={c} data-skelett-spalte="">
              <Skeleton variant="text" width="70%" />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(rows)].map((_, r) => (
          <TableRow key={r}>
            {[...Array(columns)].map((_, c) => (
              <TableCell key={c}>
                <Skeleton variant="text" width={c === 0 ? '85%' : '55%'} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (paper === 'elevation') {
    // Das schlichte `Paper` eines `TableContainer component={Paper}`
    // (`RelationsManagerTab.jsx:855`, Listenansicht): `elevation={1}` ->
    // `shadows[1]`, Theme-Radius 6 px. Kein `variant` (das waere die
    // `outlined`-Form darunter), kein `sx.borderRadius` (der Theme-Vorgabewert
    // IST der der geladenen Flaeche) — nur die Scrollform des `TableContainer`.
    return (
      <Paper
        role="status"
        aria-label={ariaLabel}
        aria-busy="true"
        sx={{ overflowX: 'auto' }}
      >
        {tabelle}
      </Paper>
    );
  }

  if (paper) {
    return (
      <Paper
        variant="outlined"
        role="status"
        aria-label={ariaLabel}
        aria-busy="true"
        // Der Radius der geladenen Flaeche: `PoAnomalies.jsx:298` schreibt
        // `borderRadius: 2`, in `sx` ein VIELFACHES von `theme.shape.borderRadius`
        // (2 × 6 = 12 px, `theme.js:323`). Hier als PIXEL-Zeichenkette: eine ZAHL
        // in `sx.borderRadius` multipliziert MUI selbst noch einmal mit
        // `shape.borderRadius` (`@mui/system` `createUnaryUnit`), das Produkt als
        // Zahl ergab 72 px statt 12 (gemessen, Probe L15). Ein rohes
        // `borderRadius: 2` waere eine neue Rule-22-Verletzung.
        sx={{ borderRadius: `${theme.shape.borderRadius * 2}px` }}
      >
        {tabelle}
      </Paper>
    );
  }

  if (framed) {
    return (
      <Panel pad="none" clip role="status" aria-label={ariaLabel} aria-busy="true">
        {tabelle}
      </Panel>
    );
  }
  return (
    <Box role="status" aria-label={ariaLabel} aria-busy="true">
      {tabelle}
    </Box>
  );
}
